import { supabase } from '../config/index_supabase';

const PORTFOLIO_TABLE = 'portfolio_profiles';
const PORTFOLIO_FIELDS = 'id, display_name, icon, color, created_at, deleted_at';
const DEFAULT_PORTFOLIO_IDS = new Set(['self', 'family']);

const requireUserId = (userId) => {
  if (typeof userId !== 'string' || !userId.trim()) {
    throw new Error('Sign in to sync your portfolios.');
  }
  return userId;
};

const requireCustomPortfolioId = (memberId) => {
  if (typeof memberId !== 'string' || !memberId.trim() || DEFAULT_PORTFOLIO_IDS.has(memberId)) {
    throw new Error('Choose a custom portfolio.');
  }
  return memberId;
};

const toMember = (row) => ({
  id: row.id,
  name: `${row.display_name}'s Portfolio`,
  displayName: row.display_name,
  customizable: true,
  icon: row.icon,
  color: row.color,
  createdAt: row.created_at,
  deletedAt: row.deleted_at,
});

const toRecord = (userId, member) => {
  const displayName = String(member?.displayName || '').trim();
  if (!displayName) throw new Error('Enter a portfolio name.');

  const record = {
    user_id: requireUserId(userId),
    id: requireCustomPortfolioId(member?.id),
    display_name: displayName,
    icon: member.icon || 'Person',
    color: member.color || 'var(--accent)',
  };
  if (member.createdAt && !Number.isNaN(Date.parse(member.createdAt))) {
    record.created_at = new Date(member.createdAt).toISOString();
  }
  return record;
};

export const fetchPortfolioProfiles = async (userId) => {
  requireUserId(userId);
  const { data, error } = await supabase
    .from(PORTFOLIO_TABLE)
    .select(PORTFOLIO_FIELDS)
    .eq('user_id', userId)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  // Include tombstones so stale browser caches cannot restore removed members.
  return (data || []).map(toMember);
};

export const migratePortfolioProfiles = async (userId, members = []) => {
  requireUserId(userId);
  const records = members
    .filter((member) => !DEFAULT_PORTFOLIO_IDS.has(member?.id) && !member?.deletedAt)
    .map((member) => toRecord(userId, member));
  if (!records.length) return;

  const { error } = await supabase
    .from(PORTFOLIO_TABLE)
    .upsert(records, { onConflict: 'user_id,id', ignoreDuplicates: true, defaultToNull: false });
  if (error) throw error;
};

export const savePortfolioProfile = async (userId, member) => {
  const record = toRecord(userId, member);
  const { data, error } = await supabase
    .from(PORTFOLIO_TABLE)
    // Omit deleted_at: a stale edit must retain an existing deletion tombstone.
    .upsert(record, { onConflict: 'user_id,id' })
    .select(PORTFOLIO_FIELDS)
    .single();
  if (error) throw error;
  if (data.deleted_at) {
    throw new Error('This portfolio was removed on another device. Refresh your portfolios.');
  }
  return toMember(data);
};

export const removePortfolioProfile = async (userId, memberId) => {
  requireUserId(userId);
  requireCustomPortfolioId(memberId);
  const { error } = await supabase
    .from(PORTFOLIO_TABLE)
    .update({ deleted_at: new Date().toISOString() })
    .eq('user_id', userId)
    .eq('id', memberId)
    .select('id')
    .single();
  if (error) throw error;
};
